/**
 * Értesítési beállítások — in-app, email, push csatornánként
 */
import { useState, useEffect, useCallback } from 'react';
import { PageHeader, Card } from '../../components/ui';
import { get, put, post } from '../../lib/api';
import { useToast } from '../../components/ui/Toast';

interface Pref {
  kind: string;
  in_app: boolean;
  email: boolean;
  push: boolean;
}

interface PrefMap { [kind: string]: { inApp: boolean; email: boolean; push: boolean } }

const GROUPS = [
  {
    label: 'Ajánlatok',
    kinds: [
      { kind: 'ajanlat_megnyitva',   label: 'Ajánlat megtekintve' },
      { kind: 'ajanlat_elfogadva',   label: 'Ajánlat elfogadva' },
      { kind: 'ajanlat_elutasitva',  label: 'Ajánlat elutasítva' },
    ],
  },
  {
    label: 'Számlák',
    kinds: [
      { kind: 'szamla_kiallitva',    label: 'Számla kiállítva' },
      { kind: 'szamla_lejart',       label: 'Lejárt számla' },
      { kind: 'invoice.overdue',     label: 'Számla lejárt (emlékeztető)' },
      { kind: 'invoice.payment_received', label: 'Befizetés érkezett' },
    ],
  },
  {
    label: 'Megrendelések',
    kinds: [
      { kind: 'megrendeles_beerkezett',      label: 'Megrendelés beérkezett' },
      { kind: 'megrendeles_visszaigazolva',  label: 'Megrendelés visszaigazolva' },
    ],
  },
  {
    label: 'Feladatok',
    kinds: [
      { kind: 'task.assigned',       label: 'Feladat hozzárendelve' },
      { kind: 'task.due_soon',       label: 'Feladat hamarosan lejár' },
      { kind: 'task.overdue',        label: 'Feladat lejárt' },
      { kind: 'task.status_changed', label: 'Feladat státusz változott' },
      { kind: 'task.commented',      label: 'Feladat megjegyzés' },
    ],
  },
  {
    label: 'Projektek',
    kinds: [
      { kind: 'projekt_hatarido',    label: 'Projekt határidő közeleg' },
      { kind: 'project.member_added', label: 'Projekttagként hozzáadva' },
    ],
  },
  {
    label: 'Rendszer',
    kinds: [
      { kind: 'fizetes_sikertelen',  label: 'Sikertelen fizetés' },
      { kind: 'trial_lejar',         label: 'Próbaidőszak lejár' },
      { kind: 'keszlet_minimum',     label: 'Készlet minimum alá esett' },
      { kind: 'nav.submission_failed', label: 'NAV beküldés sikertelen' },
    ],
  },
];

const ALL_KINDS = GROUPS.flatMap(g => g.kinds.map(k => k.kind));

function defaultPrefs(): PrefMap {
  return Object.fromEntries(ALL_KINDS.map(k => [k, { inApp: true, email: false, push: false }]));
}

export default function NotificationsSettingsPage() {
  const [prefs, setPrefs] = useState<PrefMap>(defaultPrefs());
  const [saving, setSaving] = useState(false);
  const { showToast } = useToast();

  useEffect(() => {
    get<Pref[]>('/notification-preferences').then(data => {
      if (!Array.isArray(data)) return;
      setPrefs(prev => {
        const next = { ...prev };
        for (const p of data) {
          next[p.kind] = { inApp: p.in_app ?? true, email: p.email ?? false, push: p.push ?? false };
        }
        return next;
      });
    }).catch(() => {});
  }, []);

  const toggle = (kind: string, channel: 'inApp' | 'email' | 'push') => {
    setPrefs(prev => ({
      ...prev,
      [kind]: { ...prev[kind], [channel]: !prev[kind]?.[channel] },
    }));
  };

  const save = async () => {
    setSaving(true);
    try {
      await put('/notification-preferences', {
        preferences: ALL_KINDS.map(kind => ({
          kind,
          inApp: prefs[kind]?.inApp ?? true,
          email: prefs[kind]?.email ?? false,
          push: prefs[kind]?.push ?? false,
        })),
      });
      showToast('Értesítési beállítások mentve.', 'success');
    } catch {
      showToast('Mentés sikertelen.', 'error');
    } finally {
      setSaving(false);
    }
  };

  const allOn = (channel: 'inApp' | 'email' | 'push') =>
    ALL_KINDS.every(k => prefs[k]?.[channel]);

  const toggleAll = (channel: 'inApp' | 'email' | 'push') => {
    const val = !allOn(channel);
    setPrefs(prev => Object.fromEntries(ALL_KINDS.map(k => [k, { ...prev[k], [channel]: val }])));
  };

  return (
    <div>
      <PageHeader title="Értesítési beállítások" description="Állítsd be, milyen csatornákon kapsz értesítést az egyes eseményekről." />

      {/* Push subscription card */}
      <PushCard />

      {/* Reggeli emlékeztető */}
      <MorningReminderCard />

      {/* Preference table */}
      <Card>
        <div className="card-h" style={{ display: 'flex', alignItems: 'center' }}>
          <h3 style={{ flex: 1 }}>Értesítési csatornák</h3>
          <button
            onClick={save}
            disabled={saving}
            style={{ background: 'var(--orange)', color: '#fff', border: 'none', borderRadius: '7px', padding: '7px 16px', fontSize: '13px', fontWeight: 700, cursor: saving ? 'wait' : 'pointer', opacity: saving ? 0.7 : 1, fontFamily: 'inherit' }}
          >
            {saving ? 'Mentés...' : 'Mentés'}
          </button>
        </div>

        {/* Header */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 90px 90px 90px', gap: '0', padding: '6px 0', borderBottom: '2px solid var(--line)', marginBottom: '4px' }}>
          <div style={{ fontSize: '11px', fontWeight: 700, color: 'var(--slate-2)', textTransform: 'uppercase', letterSpacing: '.06em', paddingLeft: '4px' }}>Esemény</div>
          {(['inApp', 'email', 'push'] as const).map(ch => (
            <div key={ch} style={{ textAlign: 'center' }}>
              <button
                onClick={() => toggleAll(ch)}
                style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '11px', fontWeight: 700, color: 'var(--slate-2)', textTransform: 'uppercase', letterSpacing: '.06em', fontFamily: 'inherit', padding: '2px 4px' }}
                title={`Mind ${allOn(ch) ? 'ki' : 'be'}`}
              >
                {ch === 'inApp' ? 'App' : ch === 'email' ? 'Email' : 'Push'}
              </button>
            </div>
          ))}
        </div>

        {GROUPS.map(group => (
          <div key={group.label} style={{ marginBottom: '8px' }}>
            <div style={{ fontSize: '11px', fontWeight: 700, color: 'var(--slate)', textTransform: 'uppercase', letterSpacing: '.08em', padding: '8px 4px 4px' }}>
              {group.label}
            </div>
            {group.kinds.map(({ kind, label }) => (
              <div
                key={kind}
                style={{ display: 'grid', gridTemplateColumns: '1fr 90px 90px 90px', alignItems: 'center', padding: '6px 4px', borderRadius: '6px' }}
                onMouseEnter={e => (e.currentTarget.style.background = 'var(--mist)')}
                onMouseLeave={e => (e.currentTarget.style.background = '')}
              >
                <div style={{ fontSize: '13px', color: 'var(--navy)' }}>{label}</div>
                {(['inApp', 'email', 'push'] as const).map(ch => (
                  <div key={ch} style={{ display: 'flex', justifyContent: 'center' }}>
                    <Toggle
                      checked={prefs[kind]?.[ch] ?? (ch === 'inApp')}
                      onChange={() => toggle(kind, ch)}
                    />
                  </div>
                ))}
              </div>
            ))}
          </div>
        ))}
      </Card>
    </div>
  );
}

function urlBase64ToUint8Array(base64: string): Uint8Array {
  const padding = '='.repeat((4 - (base64.length % 4)) % 4);
  const b64 = (base64 + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = atob(b64);
  return Uint8Array.from([...raw].map(c => c.charCodeAt(0)));
}

function arrayBufferToBase64(buf: ArrayBuffer): string {
  return btoa(String.fromCharCode(...new Uint8Array(buf)));
}

function PushCard() {
  // Szinkron: azonnal tudjuk a jelenlegi engedély-állapotot
  const notifSupported = typeof Notification !== 'undefined' && 'serviceWorker' in navigator && 'PushManager' in window;
  const [permission, setPermission] = useState<NotificationPermission>(
    notifSupported ? Notification.permission : 'denied',
  );
  const [subscribed, setSubscribed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { showToast } = useToast();

  // Ellenőrizzük van-e már aktív SW subscription
  useEffect(() => {
    if (!notifSupported || Notification.permission !== 'granted') return;
    navigator.serviceWorker.ready.then(reg =>
      reg.pushManager.getSubscription().then(sub => setSubscribed(!!sub))
    ).catch(() => {});
  }, [notifSupported]);

  const enablePush = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // 1. Böngésző engedély kérése — ez hozza fel a natív dialógot
      const perm = await Notification.requestPermission();
      setPermission(perm);
      if (perm !== 'granted') {
        setLoading(false);
        return;
      }

      // 2. VAPID public key lekérése
      const { enabled, publicKey } = await get<{ enabled: boolean; publicKey?: string }>('/push/vapid-key');
      if (!enabled || !publicKey) {
        setError('A push szolgáltatás nem elérhető.');
        setLoading(false);
        return;
      }

      // 3. Service Worker feliratkozás
      const reg = await Promise.race([
        navigator.serviceWorker.ready,
        new Promise<never>((_, rej) => setTimeout(() => rej(new Error('SW timeout')), 12000)),
      ]);
      const sub = await (reg as ServiceWorkerRegistration).pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(publicKey),
      });

      // 4. Feliratkozás küldése a szervernek
      await post('/push/subscribe', {
        endpoint: sub.endpoint,
        keys: {
          p256dh: arrayBufferToBase64(sub.getKey('p256dh')!),
          auth: arrayBufferToBase64(sub.getKey('auth')!),
        },
        userAgent: navigator.userAgent,
      });

      setSubscribed(true);
      showToast('Push értesítések bekapcsolva!', 'success');
    } catch (e) {
      const msg = e instanceof Error ? e.message : '';
      setError(msg.includes('timeout') ? 'Időtúllépés. Töltsd újra az oldalt és próbáld újra.' : 'Hiba történt. Ellenőrizd a böngésző beállításait.');
    } finally {
      setLoading(false);
    }
  }, [showToast]);

  const disablePush = useCallback(async () => {
    setLoading(true);
    try {
      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.getSubscription();
      if (sub) {
        await post('/push/unsubscribe', { endpoint: sub.endpoint });
        await sub.unsubscribe();
      }
      setSubscribed(false);
      showToast('Push értesítések kikapcsolva.', 'success');
    } catch {
      showToast('Kikapcsolás sikertelen.', 'error');
    } finally {
      setLoading(false);
    }
  }, [showToast]);

  if (!notifSupported) {
    return (
      <Card className="mb-4">
        <div className="card-h"><h3>Push értesítések</h3></div>
        <p style={{ fontSize: '13px', color: 'var(--slate)', margin: '8px 0' }}>Ez a böngésző nem támogatja a push értesítéseket.</p>
      </Card>
    );
  }

  return (
    <Card className="mb-4">
      <div className="card-h"><h3>Push értesítések (böngésző)</h3></div>

      <div style={{ padding: '16px', background: permission === 'granted' && subscribed ? 'rgba(34,197,94,.07)' : permission === 'denied' ? 'rgba(239,68,68,.06)' : 'var(--mist)', borderRadius: '10px', margin: '8px 0', display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap' }}>
        <div style={{ flex: 1, minWidth: '200px' }}>
          {permission === 'denied' ? (
            <>
              <div style={{ fontWeight: 700, fontSize: '13px', color: '#ef4444', marginBottom: '4px' }}>Értesítések blokkolva</div>
              <div style={{ fontSize: '12px', color: 'var(--slate)', lineHeight: 1.5 }}>
                A böngésző blokkolta az értesítéseket. Engedélyezéshez:<br />
                <strong>Chrome:</strong> kattints a lakat ikonra a böngésző-sávban → Értesítések → Engedélyezés<br />
                <strong>Firefox:</strong> kattints a pajzs ikonra → Engedélyek → Értesítések engedélyezése
              </div>
            </>
          ) : permission === 'granted' && subscribed ? (
            <>
              <div style={{ fontWeight: 700, fontSize: '13px', color: '#16a34a', marginBottom: '4px' }}>✓ Push értesítések bekapcsolva</div>
              <div style={{ fontSize: '12px', color: 'var(--slate)' }}>Ezen az eszközön/böngészőben megkapod a push értesítéseket.</div>
            </>
          ) : (
            <>
              <div style={{ fontWeight: 700, fontSize: '13px', color: 'var(--navy)', marginBottom: '4px' }}>Push értesítések bekapcsolása</div>
              <div style={{ fontSize: '12px', color: 'var(--slate)' }}>Kapj valós idejű értesítést a böngészőben, akkor is ha az oldal nincs nyitva.</div>
              {error && <div style={{ fontSize: '12px', color: '#ef4444', marginTop: '4px' }}>{error}</div>}
            </>
          )}
        </div>

        {permission !== 'denied' && (
          permission === 'granted' && subscribed ? (
            <button
              onClick={disablePush}
              disabled={loading}
              style={{ background: 'var(--line)', color: 'var(--navy)', border: 'none', borderRadius: '7px', padding: '8px 16px', fontSize: '13px', fontWeight: 600, cursor: loading ? 'wait' : 'pointer', opacity: loading ? 0.7 : 1, fontFamily: 'inherit', flexShrink: 0 }}
            >
              {loading ? 'Kikapcsolás...' : 'Kikapcsolás'}
            </button>
          ) : (
            <button
              onClick={enablePush}
              disabled={loading}
              style={{ background: 'var(--orange)', color: '#fff', border: 'none', borderRadius: '7px', padding: '10px 20px', fontSize: '14px', fontWeight: 700, cursor: loading ? 'wait' : 'pointer', opacity: loading ? 0.7 : 1, fontFamily: 'inherit', flexShrink: 0, boxShadow: '0 2px 8px rgba(244,123,32,.35)' }}
            >
              {loading ? 'Kapcsolódás...' : '🔔 Push értesítések bekapcsolása'}
            </button>
          )
        )}
      </div>

      <p style={{ fontSize: '12px', color: 'var(--slate-2)', margin: '4px 0 0' }}>
        Az alábbi táblázatban a <strong>Push</strong> oszlopban kapcsolhatod be típusonként, melyekről kapsz push értesítést.
      </p>
    </Card>
  );
}

const MORNING_REMINDER_KEY = 'modulum_morning_reminder';

function MorningReminderCard() {
  const [enabled, setEnabled] = useState(() => localStorage.getItem(MORNING_REMINDER_KEY) !== 'off');
  const notifSupported = typeof Notification !== 'undefined' && 'PushManager' in window;
  const subscribed = notifSupported && Notification.permission === 'granted';

  const toggle = () => {
    const next = !enabled;
    setEnabled(next);
    localStorage.setItem(MORNING_REMINDER_KEY, next ? 'on' : 'off');
  };

  return (
    <Card className="mb-4">
      <div className="card-h"><h3>Reggeli emlékeztető ☕</h3></div>
      <div style={{ padding: '12px 0', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap' }}>
        <div style={{ flex: 1, minWidth: 200 }}>
          <div style={{ fontSize: '13px', color: 'var(--navy)', fontWeight: 600, marginBottom: 4 }}>
            Reggeli push értesítés 8:00-kor
          </div>
          <div style={{ fontSize: '12px', color: 'var(--slate)', lineHeight: 1.5 }}>
            Ha reggel még nem nyitottad meg a Modulum appot, elküldjük a napi köszöntőt, hogy ne maradj le semmiről.
            {!subscribed && (
              <span style={{ display: 'block', marginTop: 4, color: '#f59e0b', fontWeight: 600 }}>
                ⚠️ A push értesítések még nincsenek bekapcsolva — a fenti kártyán engedélyezd először.
              </span>
            )}
          </div>
        </div>
        <Toggle checked={enabled} onChange={toggle} />
      </div>
    </Card>
  );
}

function Toggle({ checked, onChange }: { checked: boolean; onChange: () => void }) {
  return (
    <button
      onClick={onChange}
      style={{
        width: '36px', height: '20px',
        borderRadius: '10px',
        background: checked ? 'var(--orange)' : 'var(--line)',
        border: 'none',
        cursor: 'pointer',
        position: 'relative',
        transition: 'background .15s',
        flexShrink: 0,
      }}
      role="switch"
      aria-checked={checked}
    >
      <span style={{
        position: 'absolute',
        top: '3px',
        left: checked ? '18px' : '3px',
        width: '14px', height: '14px',
        borderRadius: '50%',
        background: '#fff',
        transition: 'left .15s',
        boxShadow: '0 1px 3px rgba(0,0,0,.2)',
      }} />
    </button>
  );
}
